/**
 * Errors that are environmental, not ClubEzy defects, and so are kept out of
 * Sentry (CLUB-2497).
 */

/**
 * Passed to `Sentry.init({ ignoreErrors })`; each pattern is matched against
 * the error message.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  // Outlook / Microsoft Safe Links scanners preview emailed links and throw
  // from their own injected script (CLUBEZY-AUTH-4, -5, -8).
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // In-app browsers (Facebook/Instagram, iOS webviews) inject scripts that
  // assume a native bridge exists (CLUBEZY-AUTH-6, -7).
  "undefined is not an object (evaluating 'window._META",
  "undefined is not an object (evaluating 'window.webkit.messageHandlers",
];

const CHUNK_LOAD_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
];

/**
 * A lazy route chunk that no longer exists: the tab was opened before a
 * deploy, and the hashed file it asks for has since been replaced.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return CHUNK_LOAD_PATTERNS.some((p) => p.test(message));
}

const RELOAD_KEY = 'clubezy.chunkReloadAt';
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Reload once to pick up the new deploy instead of leaving the user on a
 * broken page (CLUBEZY-AUTH-1, CLUBEZY-ADMIN-1/-2). Returns `false`, without
 * reloading, if we already reloaded moments ago. In that case the chunk is
 * genuinely missing and the error should still be reported.
 */
export function reloadForStaleChunk(
  storage:
    | Pick<Storage, 'getItem' | 'setItem'>
    | undefined = globalThis.sessionStorage,
  reload: () => void = () => globalThis.location?.reload(),
  now: number = Date.now(),
): boolean {
  try {
    const last = storage?.getItem(RELOAD_KEY);
    if (last != null && now - Number(last) < RELOAD_COOLDOWN_MS) return false;
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // Storage blocked (private mode): reloading without a guard could loop.
    return false;
  }
  reload();
  return true;
}
